import React from 'react';
import { Helmet } from 'react-helmet';
import { FaGithub } from 'react-icons/fa';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { headerData } from '../../../data/headerData';
import { projectPath } from '../../../utils/projectSlug';
import Navbar from '../../container/navbar';
import Reveal from '../../helper/reveal';
import './project-detail.css';

const SITE = 'https://miretu.vercel.app';

const Facts = ({ project }) => {
    const facts = [['Role', project.role], ['Period', project.period]].filter(([, value]) => value);
    if (facts.length === 0) return null;
    return (
        <dl className='pdetail__facts'>
            {facts.map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
            ))}
        </dl>
    );
};

function ProjectDetailUI({ slug, project, neighbours, related }) {
    if (!project) {
        return (
            <>
                <Helmet>
                    <title>{`Project not found | ${headerData.name}`}</title>
                    <meta name='robots' content='noindex' />
                </Helmet>
                <Navbar />
                <main id='main' className='pdetail'>
                    <div className='container'>
                        <span className='eyebrow'>404 / Project</span>
                        <h1 className='section-title'>That project doesn’t exist.</h1>
                        <p className='pdetail__lead'>The link may be old or mistyped.</p>
                        <Link to='/projects' className='btn btn--primary'>See all projects</Link>
                    </div>
                </main>
            </>
        );
    }

    const url = `${SITE}${projectPath(slug)}`;
    const title = `${project.projectName} | ${headerData.name}`;

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name='description' content={project.projectDesc} />
                <link rel='canonical' href={url} />
            </Helmet>
            <Navbar />
            <main id='main' className='pdetail'>
                <div className='container'>
                    <Link to='/projects' className='pdetail__back'>
                        <HiArrowLeft aria-hidden='true' /> All projects
                    </Link>

                    <Reveal className='pdetail__head'>
                        <span className='eyebrow'>Project</span>
                        <h1 className='section-title'>{project.projectName}</h1>
                        <p className='pdetail__lead'>{project.projectDesc}</p>
                        <Facts project={project} />
                    </Reveal>

                    {project.problem && (
                        <section className='pdetail__block' aria-labelledby='pd-problem'>
                            <h2 id='pd-problem'>The problem</h2>
                            <p>{project.problem}</p>
                        </section>
                    )}

                    {project.highlights && project.highlights.length > 0 && (
                        <section className='pdetail__block' aria-labelledby='pd-built'>
                            <h2 id='pd-built'>What I built</h2>
                            <ul>
                                {project.highlights.map((text) => <li key={text}>{text}</li>)}
                            </ul>
                        </section>
                    )}

                    {project.outcome && (
                        <section className='pdetail__block' aria-labelledby='pd-outcome'>
                            <h2 id='pd-outcome'>Outcome</h2>
                            <p>{project.outcome}</p>
                        </section>
                    )}

                    {project.tags && project.tags.length > 0 && (
                        <section className='pdetail__block' aria-labelledby='pd-tech'>
                            <h2 id='pd-tech'>Technologies</h2>
                            <ul className='pdetail__tags'>
                                {project.tags.map((tag) => (
                                    <li key={tag}>
                                        <Link className='chip' to={`/projects?tag=${encodeURIComponent(tag)}`}>{tag}</Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {project.code && (
                        <a className='btn' href={project.code} target='_blank' rel='noreferrer'>
                            <FaGithub aria-hidden='true' /> Source code
                        </a>
                    )}

                    {related.length > 0 && (
                        <section className='pdetail__related' aria-labelledby='pd-related'>
                            <h2 id='pd-related'>Related projects</h2>
                            <ul>
                                {related.map((entry) => (
                                    <li key={entry.slug}>
                                        <Link to={projectPath(entry.slug)}>{entry.project.projectName}</Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <nav className='pdetail__pager' aria-label='More projects'>
                        {neighbours.previous ? (
                            <Link to={projectPath(neighbours.previous.slug)} rel='prev'>
                                <HiArrowLeft aria-hidden='true' /> {neighbours.previous.project.projectName}
                            </Link>
                        ) : <span />}
                        {neighbours.next ? (
                            <Link to={projectPath(neighbours.next.slug)} rel='next'>
                                {neighbours.next.project.projectName} <HiArrowRight aria-hidden='true' />
                            </Link>
                        ) : <span />}
                    </nav>
                </div>
            </main>
        </>
    );
}

export default ProjectDetailUI;
